import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { GardenDetails, DiaryEntry, GardenPlant } from '@/types/garden';
import { useAuth } from '@/context/AuthContext';
import { attemptSilentReauth, SessionExpiredError } from '@/lib/session';

interface GardenContextValue {
  garden: GardenDetails | null;
  diary: DiaryEntry[];
  loading: boolean;
  saveError: string | null;
  clearSaveError: () => void;
  setGarden: (g: GardenDetails) => Promise<void>;
  addDiaryEntry: (entry: Omit<DiaryEntry, 'id'>) => Promise<void>;
  updateDiaryEntry: (id: string, entry: Partial<DiaryEntry>) => Promise<void>;
  deleteDiaryEntry: (id: string) => Promise<void>;
  addPlant: (plant: Omit<GardenPlant, 'id'>) => Promise<void>;
  updatePlant: (id: string, plant: Partial<GardenPlant>) => Promise<void>;
  removePlant: (id: string) => Promise<void>;
}

const GardenContext = createContext<GardenContextValue | undefined>(undefined);

const GARDEN_KEY = 'tgc_garden';
const DIARY_KEY  = 'tgc_diary';

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Dev demo data ─────────────────────────────────────────────────────────────

const DEMO_GARDEN: GardenDetails = {
  id: 'garden-1',
  userId: 'demo-user-1',
  name: 'The Kitchen Garden',
  description: 'A south-facing raised bed kitchen garden with a small polytunnel',
  type: 'raised-bed',
  zone: '8b',
  country: 'United Kingdom',
  region: 'Somerset',
  soilType: 'loam',
  soilPH: 6.5,
  aspect: 'south',
  areaSqm: 40,
  hasFrost: true,
  lastFrostDate: 'April 18',
  firstFrostDate: 'October 25',
  rainfall: 'moderate',
  notes: 'Sloped gently to the south. Large oak tree to the north provides some shelter.',
  plants: [
    { id: 'gp1', plantId: 'tomato',      plantName: 'Tomato',       quantity: 6,  status: 'sown',    datePlanted: '2026-03-10', notes: 'Sungold + Gardeners Delight' },
    { id: 'gp2', plantId: 'carrot',      plantName: 'Carrot',       quantity: 30, status: 'planned', notes: 'Nantes 5 + Purple Haze' },
    { id: 'gp3', plantId: 'kale',        plantName: 'Kale',         quantity: 8,  status: 'growing', datePlanted: '2025-09-15', notes: 'Red Russian + Cavolo Nero' },
    { id: 'gp4', plantId: 'garlic',      plantName: 'Garlic',       quantity: 40, status: 'growing', datePlanted: '2025-10-28', notes: 'Elephant + Solent Wight' },
    { id: 'gp5', plantId: 'pea',         plantName: 'Garden Pea',   quantity: 20, status: 'planned', notes: 'Alderman + Douce Provence' },
    { id: 'gp6', plantId: 'bean-runner', plantName: 'Runner Bean',  quantity: 12, status: 'planned', notes: 'Scarlet Emperor' },
  ],
  createdAt: '2024-02-01',
  updatedAt: '2026-03-12',
};

const DEMO_DIARY: DiaryEntry[] = [
  {
    id: 'd1', gardenId: 'garden-1', date: '2026-03-10',
    title: 'Sowed tomatoes and peppers under glass',
    content: 'Sowed 6 Sungold and 6 Gardeners Delight tomatoes in the heated propagator at 22°C. Also sowed 4 Padron pepper plants. The soil feels just right — warm and crumbly.\n\nNoticed the garlic is putting on good growth after the recent mild weather. The kale survived the frosts beautifully and is sweetened by them.',
    tasks: ['Sowed tomatoes', 'Sowed peppers', 'Checked garlic'],
    weather: 'Mild and overcast, 12°C',
    mood: 'great',
    plantIds: ['tomato'],
    tags: ['sowing', 'greenhouse'],
  },
  {
    id: 'd2', gardenId: 'garden-1', date: '2026-03-05',
    title: 'Prepared beds for spring',
    content: 'Spread the last of the compost heap over beds 2 and 3. The compost has broken down beautifully — very dark and crumbly with a good earthy smell. Beds 4 and 5 got a thick mulch of straw to suppress early weeds while the soil warms.\n\nOrdered seeds for the year: Nantes 5 carrots, Alderman peas, and a heritage lettuce mix.',
    tasks: ['Spread compost', 'Mulched beds 4 & 5', 'Ordered seeds'],
    weather: 'Bright and cold, 8°C',
    mood: 'good',
    tags: ['soil', 'prep'],
  },
  {
    id: 'd3', gardenId: 'garden-1', date: '2026-02-20',
    title: 'Chitted potatoes, applied 500 preparation',
    content: 'Set out the seed potatoes to chit on the windowsill — 20 Sarpo Mira maincrop and 10 Charlotte (early). Made up the 500 preparation in the evening (a root day), stirring for the full hour in warm rainwater. Sprayed on beds 1–3 in the late afternoon as the sun was lowering. The ritual of it is deeply satisfying.',
    tasks: ['Chitted potatoes', 'Applied preparation 500'],
    weather: 'Cold and clear, 6°C',
    mood: 'great',
    plantIds: ['potato'],
    tags: ['biodynamic', 'potatoes'],
  },
];

// ── API helpers ───────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
  });
  if (res.status === 401) {
    // The SWA auth cookie lapsed mid-visit. Try to renew it silently.
    attemptSilentReauth();
    throw new SessionExpiredError(path);
  }
  if (!res.ok) throw new Error(`API ${options?.method ?? 'GET'} ${path} → ${res.status}`);
  if (res.status === 204) return undefined as T;
  return res.json();
}

function saveErrorFor(err: unknown, fallback: string): string {
  return err instanceof SessionExpiredError
    ? 'Your session had expired. Signing you back in — please try again in a moment.'
    : fallback;
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function GardenProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [garden, setGardenState] = useState<GardenDetails | null>(null);
  const [diary, setDiaryState] = useState<DiaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const clearSaveError = useCallback(() => setSaveError(null), []);

  // Determine whether to use the real API or localStorage (dev mode)
  const useApi = !import.meta.env.DEV;

  // ── Load on mount / auth change ────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;

    if (!useApi) {
      // Dev: localStorage → demo data fallback
      const storedGarden = localStorage.getItem(GARDEN_KEY);
      const storedDiary  = localStorage.getItem(DIARY_KEY);
      setGardenState(storedGarden ? JSON.parse(storedGarden) : DEMO_GARDEN);
      setDiaryState(storedDiary  ? JSON.parse(storedDiary)  : DEMO_DIARY);
      setLoading(false);
      return;
    }

    if (!user) {
      setGardenState(null);
      setDiaryState([]);
      setLoading(false);
      return;
    }

    // Production + authenticated: fetch from API
    Promise.all([
      apiFetch<GardenDetails | null>('/api/gardens'),
      apiFetch<DiaryEntry[]>('/api/diary'),
    ])
      .then(([g, d]) => {
        setGardenState(g);
        setDiaryState(d ?? []);
        // Mirror to localStorage for offline resilience
        if (g) localStorage.setItem(GARDEN_KEY, JSON.stringify(g));
        if (d) localStorage.setItem(DIARY_KEY, JSON.stringify(d));
      })
      .catch(err => {
        if (err instanceof SessionExpiredError) {
          // Not an outage — we're signed out. Showing the cached copy here is
          // what used to make an expired session look like a live one, right up
          // until a save failed. Leave it empty; a re-auth bounce is in flight.
          console.warn('[GardenContext] session expired during load');
          setGardenState(null);
          setDiaryState([]);
          return;
        }
        console.error('[GardenContext] API load failed, falling back to localStorage', err);
        const storedGarden = localStorage.getItem(GARDEN_KEY);
        const storedDiary  = localStorage.getItem(DIARY_KEY);
        if (storedGarden) setGardenState(JSON.parse(storedGarden));
        if (storedDiary)  setDiaryState(JSON.parse(storedDiary));
      })
      .finally(() => setLoading(false));
  }, [authLoading, user?.id, useApi]);

  // ── Garden ─────────────────────────────────────────────────────────────────
  const setGarden = useCallback(async (g: GardenDetails) => {
    setGardenState(g);
    localStorage.setItem(GARDEN_KEY, JSON.stringify(g));
    if (useApi) {
      try {
        const saved = await apiFetch<GardenDetails>('/api/gardens', {
          method: 'POST',
          body: JSON.stringify(g),
        });
        // Update state with server response (may include updatedAt etc.)
        setGardenState(saved);
        localStorage.setItem(GARDEN_KEY, JSON.stringify(saved));
      } catch (err) {
        console.error('[GardenContext] garden save failed', err);
        setSaveError(saveErrorFor(err, 'Garden failed to save. Please try again.'));
      }
    }
  }, [useApi]);

  // ── Diary ──────────────────────────────────────────────────────────────────
  const addDiaryEntry = useCallback(async (entry: Omit<DiaryEntry, 'id'>) => {
    if (useApi) {
      try {
        const created = await apiFetch<DiaryEntry>('/api/diary', {
          method: 'POST',
          body: JSON.stringify(entry),
        });
        const updated = [created, ...diary].sort((a, b) => b.date.localeCompare(a.date));
        setDiaryState(updated);
        localStorage.setItem(DIARY_KEY, JSON.stringify(updated));
      } catch (err) {
        console.error('[GardenContext] diary create failed', err);
        setSaveError(saveErrorFor(err, 'Diary entry failed to save. Please try again.'));
      }
    } else {
      const newEntry = { ...entry, id: uid() };
      const updated = [newEntry, ...diary].sort((a, b) => b.date.localeCompare(a.date));
      setDiaryState(updated);
      localStorage.setItem(DIARY_KEY, JSON.stringify(updated));
    }
  }, [useApi, diary]);

  const updateDiaryEntry = useCallback(async (id: string, data: Partial<DiaryEntry>) => {
    const updated = diary.map(e => e.id === id ? { ...e, ...data } : e);
    setDiaryState(updated);
    localStorage.setItem(DIARY_KEY, JSON.stringify(updated));
    if (useApi) {
      try {
        await apiFetch(`/api/diary/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      } catch (err) {
        console.error('[GardenContext] diary update failed', err);
        setSaveError(saveErrorFor(err, 'Diary entry failed to update. Please try again.'));
      }
    }
  }, [useApi, diary]);

  const deleteDiaryEntry = useCallback(async (id: string) => {
    const updated = diary.filter(e => e.id !== id);
    setDiaryState(updated);
    localStorage.setItem(DIARY_KEY, JSON.stringify(updated));
    if (useApi) {
      try {
        await apiFetch(`/api/diary/${id}`, { method: 'DELETE' });
      } catch (err) {
        console.error('[GardenContext] diary delete failed', err);
        setSaveError(saveErrorFor(err, 'Diary entry failed to delete. Please try again.'));
      }
    }
  }, [useApi, diary]);

  // ── Plants (stored inside the garden document) ─────────────────────────────
  const addPlant = useCallback(async (plant: Omit<GardenPlant, 'id'>) => {
    if (!garden) return;
    const newPlant = { ...plant, id: uid() };
    await setGarden({ ...garden, plants: [...garden.plants, newPlant] });
  }, [garden, setGarden]);

  const updatePlant = useCallback(async (id: string, data: Partial<GardenPlant>) => {
    if (!garden) return;
    await setGarden({ ...garden, plants: garden.plants.map(p => p.id === id ? { ...p, ...data } : p) });
  }, [garden, setGarden]);

  const removePlant = useCallback(async (id: string) => {
    if (!garden) return;
    await setGarden({ ...garden, plants: garden.plants.filter(p => p.id !== id) });
  }, [garden, setGarden]);

  return (
    <GardenContext.Provider value={{
      garden, diary, loading, saveError, clearSaveError,
      setGarden, addDiaryEntry, updateDiaryEntry, deleteDiaryEntry,
      addPlant, updatePlant, removePlant,
    }}>
      {children}
    </GardenContext.Provider>
  );
}

export function useGarden() {
  const ctx = useContext(GardenContext);
  if (!ctx) throw new Error('useGarden must be used within GardenProvider');
  return ctx;
}
